import type { Server, ServerData } from '@/types/server';
import { apiRequest } from '@/lib/api';

const API_BASE = '/api/project';

export interface ServersApiResponse {
  servers: Server[];
  project: {
    id: string;
    hashid: string;
    name: string;
    has_active_subscription: boolean;
    is_subscription_ending: boolean;
    is_subscription_cancelled: boolean;
    subscription_end_date: string | null;
    billing_plan: string;
  };
  server_data: ServerData;
  ssh_public_key: string;
}

export async function fetchServers(projectId: string): Promise<ServersApiResponse> {
  return apiRequest(`${API_BASE}/${projectId}/servers`);
}

export interface ServerMetrics {
  cpu_usage: number | null;
  cpu_cores: number | null;
  memory_used: number | null;
  memory_total: number | null;
  disk_used: number | null;
  disk_total: number | null;
  uptime_formatted: string | null;
}

export interface ServerMetricsResponse {
  metrics: ServerMetrics;
}

export interface ServerMetricsHistory {
  cpu: number[];
  timestamps?: string[];
}

export interface ServerMetricsHistoryResponse {
  history: ServerMetricsHistory;
}

type ServerPowerResponse = {
  success?: boolean;
  message?: string;
  error?: string;
};

export async function fetchServerMetrics(serverId: number): Promise<ServerMetricsResponse> {
  try {
    const data = await apiRequest<{ metrics?: ServerMetrics }>(`/api/v1/servers/${serverId}/metrics`);
    return {
      metrics: data.metrics ?? {
        cpu_usage: null,
        cpu_cores: null,
        memory_used: null,
        memory_total: null,
        disk_used: null,
        disk_total: null,
        uptime_formatted: null,
      },
    };
  } catch {
    return {
      metrics: {
        cpu_usage: null,
        cpu_cores: null,
        memory_used: null,
        memory_total: null,
        disk_used: null,
        disk_total: null,
        uptime_formatted: null,
      },
    };
  }
}

export async function fetchServerMetricsHistory(
  serverId: number,
  range: '1h' | '1d' | '1w'
): Promise<ServerMetricsHistoryResponse> {
  try {
    const data = await apiRequest<{ history?: ServerMetricsHistory }>(`/api/v1/servers/${serverId}/metrics/history?range=${range}`);
    return {
      history: data.history ?? { cpu: [], timestamps: [] },
    };
  } catch {
    return {
      history: { cpu: [], timestamps: [] },
    };
  }
}

async function postServerPowerAction(
  projectId: string,
  serverId: number,
  action: 'reboot' | 'reset'
): Promise<{ success: boolean; message: string }> {
  const defaultMessage = action === 'reset' ? 'Force reboot initiated' : 'Reboot initiated';
  
  const data = await apiRequest<ServerPowerResponse>(`${API_BASE}/${projectId}/servers/${serverId}/${action}`, {
    method: 'POST',
  });
  
  return {
    success: data.success ?? true,
    message: data.message ?? defaultMessage,
  };
}

export async function checkServerAvailability(
  projectId: string,
  serverType: string, 
  location: string
): Promise<{
  available: boolean;
  error?: string;
  message?: string;
}> {
  try {
    return await apiRequest(
      `${API_BASE}/${projectId}/servers/check-availability?server_type=${serverType}&location=${location}`
    );
  } catch {
    return { available: true };
  }
}

export async function updateServerDisplayName(
  projectId: string,
  serverId: number,
  displayName: string
): Promise<{ success: boolean; display_name: string }> {
  return apiRequest(`${API_BASE}/${projectId}/servers/${serverId}/edit`, {
    method: 'POST',
    body: JSON.stringify({ display_name: displayName }),
  });
}

export async function toggleSecurityUpdates(
  projectId: string,
  serverId: number,
  enabled: boolean
): Promise<{ success: boolean; automatic_security_updates: boolean }> {
  return apiRequest(`${API_BASE}/${projectId}/servers/${serverId}/toggle-security-updates`, {
    method: 'POST',
    body: JSON.stringify({ enable_security_updates: enabled }),
  });
}

export async function rebootServer(
  projectId: string, 
  serverId: number
): Promise<{ success: boolean; message: string }> {
  return postServerPowerAction(projectId, serverId, 'reboot');
}

export async function forceRebootServer(
  projectId: string, 
  serverId: number
): Promise<{ success: boolean; message: string }> {
  return postServerPowerAction(projectId, serverId, 'reset');
}

export async function destroyServer(
  projectId: string, 
  serverId: number
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`${API_BASE}/${projectId}/servers/${serverId}/destroy`, {
    method: 'POST',
  });
}

export interface CreateServerPayload {
  selected_provider: string;
  cpu_type?: string;
  region?: string;
  server_plan?: string;
  hostname?: string;
  port?: string | number;
  username?: string;
  auth_type?: string;
  password?: string;
}

export interface CreateServerResponse {
  success?: boolean;
  redirect_url?: string;
  error?: string;
  message?: string;
}

export async function createServer(
  projectId: string,
  payload: CreateServerPayload
): Promise<CreateServerResponse> {
  return apiRequest(`${API_BASE}/${projectId}/servers/create`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function addSSHKey(
  projectId: string, 
  serverId: number, 
  sshKey: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`${API_BASE}/${projectId}/servers/${serverId}/add-ssh-key`, {
    method: 'POST',
    body: JSON.stringify({ ssh_key: sshKey }),
  });
}

// Check server availability (uses the global API endpoint)
export async function checkServerPlanAvailability(
  serverType: string,
  location: string
): Promise<{
  available: boolean;
  error?: string;
  message?: string;
}> {
  try {
    return await apiRequest(
      `/api/project/check-server-availability?server_type=${serverType}&location=${location}`
    );
  } catch {
    return { available: true };
  }
}

// Create a new doploy cloud server
export async function createDoployServer(
  projectId: string,
  data: {
    selected_provider: 'doploy';
    cpu_type: 'shared' | 'dedicated';
    region: string;
    server_plan: string;
  }
): Promise<{ success: boolean; redirect_url?: string; error?: string }> {
  return apiRequest(`${API_BASE}/${projectId}/servers/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// Create a custom SSH server
export async function createCustomSSHServer(
  projectId: string,
  data: {
    selected_provider: 'CustomSSH';
    hostname: string;
    port: string;
    username: string;
    auth_type: 'key' | 'password';
    password?: string;
  }
): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/${projectId}/servers/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// Note: For creating servers, the existing form-based flow with Stripe checkout is preserved
// The React app will redirect to the existing add server route for the payment flow
export function getAddServerUrl(projectId: string): string {
  return `${API_BASE}/${projectId}/servers/add`;
}
